/**
 * O índice das seções e a navegação entre elas, dentro da aba Exame (E205.F2.T01, ADR-0147 §2).
 *
 * O índice fica à esquerda do documento, recolhível, e marca a seção à vista. "Anterior" e "Próxima",
 * com "n de N", ficam no alto e no fim da seção. Tudo leva pelo endereço (`linkDa`), como os links de
 * fora: a mesa escolhe a seção e rola até ela. Para ler o exame seguido, a prévia em PDF.
 *
 * E216.F6.T01 (GUS-317, QA de 03/11): ao rolar o exame, o índice e a navegação do alto ficam presos
 * no alto da tela (`sticky`); o perito não precisa voltar ao topo para trocar de seção. O índice mais
 * alto que a tela rola por dentro. A navegação do fim fica no fim.
 */
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { Link, type To } from 'react-router-dom';

import { Button, buttonVariants } from '@/components/ui';
import { ptBR } from '@/i18n/pt-BR';
import type { SecaoDoDocumentoData } from '@/lib/documentoDaSaida';
import { cn } from '@/lib/utils';

const t = ptBR.documentoDaMesa.secoes;

export function nomeDaSecao(secao: SecaoDoDocumentoData): string {
  if (secao.titulo === null) return t.semTitulo;
  return secao.numero !== null ? `${secao.numero} ${secao.titulo}` : secao.titulo;
}

const ITEM = 'block border-l-2 px-sc-2 py-sc-1 text-caption no-underline';
const ATIVO = `${ITEM} border-accent-700 bg-accent-50 font-medium text-accent-700`;
const INATIVO = `${ITEM} border-transparent text-fg-muted hover:text-fg`;

/** A coluna do índice presa no alto da tela enquanto o exame rola (E216.F6.T01). */
const COLUNA_FIXA =
  'lg:sticky lg:top-sc-4 lg:self-start lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto';
/** A navegação do alto presa no alto da tela, sobre o fundo, acima do texto que passa por baixo. */
const NAVEGACAO_FIXA = 'sticky top-0 z-10 bg-bg py-sc-1';

export interface IndiceDasSecoesProps {
  secoes: readonly SecaoDoDocumentoData[];
  ativa: string | null;
  linkDa: (ancora: string) => To;
}

export interface ColunaDoIndiceProps extends IndiceDasSecoesProps {
  aberto: boolean;
  onAlternar: () => void;
}

/** A coluna à esquerda do documento: o botão que recolhe e mostra, e o índice. */
export function ColunaDoIndice({
  aberto,
  onAlternar,
  ...indice
}: ColunaDoIndiceProps): JSX.Element {
  return (
    <div className={COLUNA_FIXA} data-testid="indice-das-secoes-coluna">
      <Button
        type="button"
        variant="ghost"
        size="sm"
        aria-expanded={aberto}
        onClick={onAlternar}
        data-testid="indice-das-secoes-alternar"
      >
        {aberto ? t.recolher : t.mostrar}
      </Button>
      {aberto ? (
        <div className="mt-sc-2">
          <IndiceDasSecoes {...indice} />
        </div>
      ) : null}
    </div>
  );
}

export function IndiceDasSecoes({ secoes, ativa, linkDa }: IndiceDasSecoesProps): JSX.Element {
  return (
    <nav aria-label={ptBR.documentoDaMesa.indice} data-testid="indice-das-secoes">
      <ol className="space-y-px">
        {secoes.map((secao) => (
          <li key={secao.ancora}>
            <Link
              to={linkDa(secao.ancora)}
              replace
              className={secao.ancora === ativa ? ATIVO : INATIVO}
              aria-current={secao.ancora === ativa ? 'location' : undefined}
              data-testid={`indice-secao-${secao.ancora}`}
            >
              {nomeDaSecao(secao)}
            </Link>
          </li>
        ))}
      </ol>
    </nav>
  );
}

function Passo({
  para,
  texto,
  direcao,
  testId,
}: {
  para: To | null;
  texto: string;
  direcao: 'anterior' | 'proxima';
  testId: string;
}): JSX.Element {
  const icone =
    direcao === 'anterior' ? (
      <ChevronLeft className="h-4 w-4" aria-hidden="true" />
    ) : (
      <ChevronRight className="h-4 w-4" aria-hidden="true" />
    );
  const conteudo =
    direcao === 'anterior' ? (
      <>
        {icone}
        {texto}
      </>
    ) : (
      <>
        {texto}
        {icone}
      </>
    );
  const classe = cn(buttonVariants({ variant: 'ghost', size: 'sm' }), 'gap-1 no-underline');
  if (para === null) {
    return (
      <span
        className={cn(classe, 'pointer-events-none opacity-40')}
        aria-disabled="true"
        data-testid={testId}
      >
        {conteudo}
      </span>
    );
  }
  return (
    <Link to={para} replace className={classe} data-testid={testId}>
      {conteudo}
    </Link>
  );
}

export interface NavegacaoDasSecoesProps extends IndiceDasSecoesProps {
  /** Onde fica (no alto ou no fim da seção): o `data-testid`; a do alto fica presa ao rolar. */
  lugar: 'alto' | 'fim';
}

export function NavegacaoDasSecoes({
  secoes,
  ativa,
  linkDa,
  lugar,
}: NavegacaoDasSecoesProps): JSX.Element | null {
  const posicao = secoes.findIndex((s) => s.ancora === ativa);
  if (posicao < 0 || secoes.length < 2) return null;
  const anterior = secoes[posicao - 1];
  const proxima = secoes[posicao + 1];
  return (
    <nav
      aria-label={t.navegacao}
      className={cn(
        'mx-auto flex max-w-[56rem] items-center justify-between gap-sc-3',
        lugar === 'alto' && NAVEGACAO_FIXA,
      )}
      data-testid={`navegacao-das-secoes-${lugar}`}
    >
      <Passo
        para={anterior !== undefined ? linkDa(anterior.ancora) : null}
        texto={t.anterior}
        direcao="anterior"
        testId={`secao-anterior-${lugar}`}
      />
      <span className="text-caption text-fg-muted" data-testid={`secao-posicao-${lugar}`}>
        {t.posicao.replace('{n}', String(posicao + 1)).replace('{total}', String(secoes.length))}
      </span>
      <Passo
        para={proxima !== undefined ? linkDa(proxima.ancora) : null}
        texto={t.proxima}
        direcao="proxima"
        testId={`secao-proxima-${lugar}`}
      />
    </nav>
  );
}
